/* eslint-disable react/no-multi-comp */
import React, {useCallback, useMemo, useRef, useContext} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import styled from 'styled-components';
import moment from 'moment/moment';
import {IconoirStatsDownSquare} from '@ey-xd/motif-icon';
import {isNullOrUndefined, labels} from '../../../../../../../util/utils';
import {useUserPreferences} from '../../../../../../../util/customHooks';
import Utility from '../../../../../../../util/utilityfunctions';
import {guidanceType, trialBalanceType} from '../../../../../../../util/uiconstants';
import AgGridWrapper from '../../../../../BaseComponentWrappers/AgGridWrapper';
import Icon from '../../../../../BaseComponentWrappers/MotifIcon';
import {getAccountsForALRA} from '../../../../../../../selectors/Ares/accounts';
import {HelixIntegrationContext} from '../../../../../../Main/Ares/Helix/HelixIntegrationModal/index';
import AccountTypeCellRenderer from './AccountTypeCellRenderer';
import RiskAccountCellRenderer from './RiskAccountCellRenderer';
import AccountNameCellRenderer from './AccountNameCellRenderer';
import ALRAColumnChooser from './ALRAColumnChooser';
import AnalysisPeriodBalanceRenderer from './AnalysisPeriodBalanceRenderer';
import XTECellRenderer from './XTECellRenderer';
import PercentageChangeRenderer from './PercentageChangeRenderer';
import OARTableFooter from '../../../OARTableFooter';
import {isEqual} from 'lodash';
import { HeaderNameRenderer} from './ALRAHeaderNameRenderer';

export default function ALRADesignations({bodyContent, engagement, selectedStEntityId}) {
	const userPreferences = useUserPreferences();

	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects || false),
		getDataRow = useSelector((state) => getAccountsForALRA(state));

	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);

	const trialBalanceData = useMemo(() => {
		const entity = getDataRow?.find((row) =>
			row?.trialBalanceData?.some((data) => data?.stEntityId === selectedStEntityId)
		);

		return entity?.trialBalanceData?.find((data) => data?.stEntityId === selectedStEntityId) || null;
	}, [selectedStEntityId, getDataRow]);

	const formatDate = (value) => {
		if (value) {
			const dateFormat = userPreferences.dateFormat ? userPreferences.dateFormat : 0;
			const userFormattedDate = moment(value).format(Utility.convertUserPreferencesDateFormatToMoment(dateFormat));
			return userFormattedDate;
		} else {
			return '-';
		}
	};

	const columnDefs = useMemo(() => {
		let defaultColumnDefs = [
			{
				field: 'accountName',
				headerName: `${labels.ALRAAccountDisclosure}`,
				cellRenderer: 'accountNameCellRenderer',
				minWidth: window.innerWidth > 1280 ? '690' : '500',
				hide: false,
				baseColumn: true,
				order: 1
			},
			{
				field: 'accountTypeId',
				headerName: `${labels.ALRADesignation}`,
				cellRenderer: 'accountTypeCellRenderer',
				maxWidth: '200',
				hide: false,
				baseColumn: true,
				order: 2
			},
			{
				field: 'assertions',
				headerName: `${labels.ALRARisks}`,
				cellRenderer: 'riskAccountCellRenderer',
				maxWidth: '150',
				hide: false,
				baseColumn: true,
				order: 3
			},
			{
				field: 'id',
				headerName: ``,
				cellRenderer: 'displayAnalyticsIcon',
				cellRendererParams: {
					bodyContent: bodyContent,
					engagement: engagement,
					hasHelixProjects: hasHelixProjects,
					isPrimary: helixConnection?.isPrimary,
					selectedStEntityId: selectedStEntityId
				},
				maxWidth: '50',
				hide: false,
				baseColumn: true,
				pinned: 'right',
				order: 11
			},
			{
				field: 'analysisperiod',
				headerName: `${labels.ALRAAnalysisPeriod}`,
				headerComponent: 'headerNameRenderer',
				headerComponentParams: {
					name: labels.ALRAAnalysisPeriod,
					date: formatDate(trialBalanceData?.analysisPeriodDate),
					addDate: true
				},
				cellRenderer: 'AnalysisPeriodBalanceRenderer',
				cellRendererParams: {
					field: 'analysisPeriodBalance',
					showClientModal: true,
					selectedStEntityId: selectedStEntityId
				},
				maxWidth: '200',
				hide: false,
				baseColumn: false,
				order: 4
			},
			{
				field: 'xTE',
				headerName: `${labels.ALRAxTE}`,
				maxWidth: '100',
				cellRenderer: 'XTECellRenderer',
				cellRendererParams: {
					selectedStEntityId: selectedStEntityId
				},
				hide: false,
				baseColumn: false,
				order: 5
			},
			{
				field: 'percentageChangeFrom1',
				headerName: `${labels.ALRAPercentageChangeFrom}`,
				headerComponent: 'headerNameRenderer',
				headerComponentParams: {
					name: labels.ALRAPercentageChangeFrom,
					date: formatDate(trialBalanceData?.comparativePeriodDate),
					addDate: true
				},
				cellRenderer: 'PercentageChangeRenderer',
				cellRendererParams: {
					field: 'analysisComparativeBalanceChangePercentage',
					selectedStEntityId: selectedStEntityId
				},
				maxWidth: '200',
				hide: false,
				baseColumn: false,
				order: 5
			},
			{
				field: 'comparativeperiod1',
				headerName: `${labels.ALRAComparativePeriod1}`,
				headerComponent: 'headerNameRenderer',
				headerComponentParams: {
					name: labels.ALRAComparativePeriod1,
					date: formatDate(trialBalanceData?.comparativePeriodDate),
					addDate: true
				},
				cellRenderer: 'AnalysisPeriodBalanceRenderer',
				cellRendererParams: {
					field: 'comparativePeriodBalance',
					selectedStEntityId: selectedStEntityId
				},
				maxWidth: '200',
				hide: true,
				baseColumn: false,
				order: 6
			},
			{
				field: 'priorAccountTypeID',
				headerName: `${labels.ALRAPriorPeriodDesignation}`,
				headerComponent: 'headerNameRenderer',
				headerComponentParams: {name: labels.priorPeriod, date: labels.ALRADesignation.toLowerCase(), addDate: false},
				maxWidth: '200',
				cellRenderer: 'accountTypeCellRenderer',
				hide: true,
				baseColumn: false,
				order: 9
			},
			{
				field: 'priorHasEstimate',
				headerName: `${labels.ALRAPriorPeriodEstimate}`,
				headerComponent: 'headerNameRenderer',
				headerComponentParams: {name: labels.priorPeriod, date: labels.estimate.toLowerCase(), addDate: false},
				maxWidth: '100',
				cellRenderer: 'booleanRenderer',
				hide: true,
				baseColumn: false,
				order: 10
			}
		];

		if (trialBalanceData?.secondComparativePeriodDate) {
			defaultColumnDefs = defaultColumnDefs.concat([
				{
					field: 'comparativeperiod2',
					headerName: `${labels.ALRAComparativePeriod2}`,
					headerComponent: 'headerNameRenderer',
					headerComponentParams: {
						name: labels.ALRAComparativePeriod2,
						date: formatDate(trialBalanceData?.secondComparativePeriodDate),
						addDate: true
					},
					cellRenderer: 'AnalysisPeriodBalanceRenderer',
					cellRendererParams: {
						field: 'secondComparativePeriodBalance',
						selectedStEntityId: selectedStEntityId
					},
					maxWidth: '200',
					hide: true,
					baseColumn: false,
					order: 7
				},
				{
					field: 'percentageChangeFrom2',
					headerName: `${labels.ALRAPercentageChangeFrom}`,
					headerComponent: 'headerNameRenderer',
					cellRenderer: 'PercentageChangeRenderer',
					headerComponentParams: {
						name: labels.ALRAPercentageChangeFrom,
						date: formatDate(trialBalanceData?.secondComparativePeriodDate),
						addDate: true
					},
					cellRendererParams: {
						field: 'analysisSecondComparativeBalanceChangePercentage',
						selectedStEntityId: selectedStEntityId
					},
					maxWidth: '200',
					hide: true,
					baseColumn: false,
					order: 8
				}
			]);
		}
		defaultColumnDefs = defaultColumnDefs.sort((a, b) => {
			return a.order - b.order;
		});
		return defaultColumnDefs;
	}, [trialBalanceData]);

	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);

	const gridRef = useRef();

	const handleOnGridReady = useCallback((params) => {
		gridRef.current = {
			api: params.api
		};

		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);
		if (grids && grids.length > 0) {
			grids.forEach((grid) => {
				grid.querySelector('.ag-body-viewport').classList.add('customScrollbar');
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 500);
	};

	const noRowsOverlayComponent = () => {
		return <span>{selectedStEntityId > 0 ? labels.StEntityNoRecords : labels.ALRANoRecords}</span>;
	};

	return (
		<StyledALRADesignations className="StyledALRADesignations">
			<section className="ColumnChooser">
				<ALRAColumnChooser
					columDefs={columnDefs}
					numberOfColsAllowedToSelect={3}
					label={labels.ALRASelectUpToThreeOptions}
					gridRef={gridRef}
				/>
			</section>
			<section style={containerStyle} className="AGGridWrapperSection">
				<AgGridWrapper
					rowData={getDataRow}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					animateRows
					noRowsOverlayComponent={noRowsOverlayComponent}
					suppressRowClickSelection
					suppressCellFocus
					wrapHeaderText
					autoHeaderHeight
					suppressContextMenu
					onReady={handleOnGridReady}
					domLayout="normal"
					components={{
						accountNameCellRenderer: AccountNameCellRenderer,
						accountTypeCellRenderer: AccountTypeCellRenderer,
						riskAccountCellRenderer: RiskAccountCellRenderer,
						headerNameRenderer: HeaderNameRenderer,
						AnalysisPeriodBalanceRenderer: AnalysisPeriodBalanceRenderer,
						PercentageChangeRenderer: PercentageChangeRenderer,
						XTECellRenderer: XTECellRenderer,
						displayAnalyticsIcon: displayAnalyticsIcon,
						booleanRenderer: BooleanRenderer
					}}
				/>
				{/* </Fetch> */}
				{trialBalanceData && (
					<section className="footer">
						<OARTableFooter
							trialBalanceAttributeJson={{
								currencyType: trialBalanceData?.currencyType,
								currencyCode: trialBalanceData?.currencyCode,
								businessUnit: trialBalanceData?.businessUnits,
								isAllBusinessUnitSelected: trialBalanceData?.isAllBusinessUnitSelected
							}}
						/>
					</section>
				)}
			</section>
		</StyledALRADesignations>
	);
}

const StyledALRADesignations = styled.section`
	position: relative;
	.ColumnChooser {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		position: absolute;
		right: var(--px-20);
		top: calc(var(--px-60) * -1);
	}
	.AGGridWrapperSection {
		.ALRADesignationLoader {
			width: 100%;
			height: auto;
		}
		.ag-root-wrapper {
			&.ag-layout-normal {
				border-radius: var(--px-8) var(--px-8) 0 0 !important;
			}
		}
		.ag-pinned-right-header {
			.ag-header-row {
				height: var(--px-40) !important;
				.ag-header-cell {
					font-size: var(--px-14);
					font-weight: 400;
					height: var(--px-40) !important;
				}
			}
		}
		.ag-ltr {
			.ag-row-group-indent-0,
			.ag-row-group-indent-1 {
				align-items: center;
				.ag-group-expanded,
				.ag-group-contracted {
					min-height: var(--px-22);
					height: auto;
					margin-right: var(--px-5);
					.ag-icon {
						&::before {
							fill: var(--neutrals-900);
							color: var(--neutrals-900);
						}
					}
				}
			}
			.ag-row-group-indent-0 {
				.ag-group-value {
					width: 100%;
				}
			}
			.ag-row-group-indent-1 {
				padding-left: 0;
				width: 100%;
				.ag-group-value {
					width: 100%;
				}
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-24);
			}
		}
		.ag-header {
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						.ag-header-cell {
							.ag-header-cell-text {
								font-size: var(--px-14);
								font-weight: 400;
							}
							&[col-id='accountName'] {
								padding-left: var(--px-24);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='accountTypeId'] {
								padding-left: var(--px-20);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='assertions'] {
								padding-left: var(--px-25);
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
							&[col-id='analysisperiod'] {
								padding-right: var(--px-10);
								.ag-header-cell-comp-wrapper,
								.ag-header-cell-label {
									justify-content: flex-end !important;
								}
								.StyledHeaderNameRenderer {
									max-width: var(--px-130);
								}
							}
							&[col-id='xTE'] {
								padding-right: var(--px-10);
								.ag-header-cell-label {
									justify-content: right !important;
								}
							}
							&[col-id='percentageChangeFrom1'] {
								padding-right: var(--px-10);
								.ag-header-cell-comp-wrapper,
								.ag-header-cell-label {
									justify-content: right !important;
								}
								.StyledHeaderNameRenderer {
									max-width: var(--px-180);
								}
							}
							&[col-id='comparativeperiod1'] {
								padding-right: var(--px-10);
								.ag-header-cell-comp-wrapper,
								.ag-header-cell-label {
									justify-content: right !important;
								}
							}
							&[col-id='comparativeperiod2'] {
								padding-right: var(--px-10);
								.ag-header-cell-comp-wrapper,
								.ag-header-cell-label {
									justify-content: right !important;
								}
							}
							&[col-id='percentageChangeFrom2'] {
								padding-right: var(--px-10);
								.ag-header-cell-comp-wrapper,
								.ag-header-cell-label {
									justify-content: right !important;
								}
							}
							&[col-id='priorAccountTypeID'] {
								.ag-header-cell-label {
									justify-content: flex-start !important;
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-full-width-container {
				.ag-row {
					&.ag-row-level-3 {
						padding: 0 var(--px-110) !important;
					}
				}
			}
			.ag-row {
				&:last-child {
					border-bottom: 0;
				}
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='accountName'] {
							.ag-cell-value {
								font-weight: 400;
								line-height: var(--px-24);
								color: var(--neutrals-700);
								.motif-tooltip-wrapper {
									display: flex;
									width: 100%;
									.motif-tooltip-trigger-wrap {
										width: auto;
										max-width: 100%;
									}
								}
							}
						}
					}
				}
				.ag-cell {
					&[col-id='accountName'] {
						padding-left: var(--px-24);
						color: var(--neutrals-900);
						text-align: left;
						display: inline-flex;
						align-items: center;
						width: 100%;
					}
					.ag-cell-wrapper {
						width: 100%;
						.ag-cell-value {
							width: 100%;
						}
					}
					&[col-id='accountTypeId'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-20);
						display: inline-flex;
						align-items: center;
					}
					&[col-id='assertions'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-20);
						display: inline-flex;
						align-items: center;
					}
					&[col-id='analysisperiod'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-10);
						padding-right: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='xTE'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-right: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='percentageChangeFrom1'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-right: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='comparativeperiod1'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-right: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='comparativeperiod2'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-right: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='percentageChangeFrom2'] {
						color: var(--neutrals-900);
						text-align: left;
						padding-left: var(--px-10);
						display: inline-flex;
						align-items: center;
						justify-content: flex-end;
					}
					&[col-id='id'] {
						display: inline-flex;
						.ag-cell-value {
							display: inline-flex;
							justify-content: center;
						}
					}
					&[col-id='priorHasEstimate'] {
						display: inline-flex;
						.ag-cell-value {
							display: inline-flex;
							justify-content: left;
						}
					}
					&[col-id='priorAccountTypeID'] {
						display: inline-flex;
						.ag-cell-value {
							display: inline-flex;
							justify-content: left;
						}
					}
					.ag-cell-value {
						.assertion-item {
							display: inline-flex;
							justify-content: center;
							align-items: center;
							margin: 0 !important;
							.craBadgesWrapper {
								margin: 0 !important;
								.assertion {
									margin: 0 !important;
								}
							}
						}
					}
				}
			}
		}
		.footer {
			display: flex;
			padding: var(--px-12);
			align-items: center;
			border-radius: 0 0 var(--px-8) var(--px-8);
			border: var(--px-1) solid var(--neutrals-200);
			border-top: 0 !important;
			background: var(--neutrals-00white);
		}
	}
`;

const BooleanRenderer = (params) => {
	if (!isNullOrUndefined(params.value)) {
		return <section>{params.value ? labels.yes : labels.no}</section>;
	} else return <span className="noDataDash">{'-'}</span>;
};

const displayAnalyticsIcon = (params) => {
	const {hasHelixProjects, selectedStEntityId, isPrimary, engagement} = params;
	const hasHelixComponentGuidance =
		params.bodyContent?.guidances?.find((g) => g.guidanceTypeId === guidanceType.HelixComponent) !== undefined;

	const guidances = params.bodyContent?.guidances
		?.filter((g) => g.guidanceTypeId === guidanceType.HelixComponent)
		.sort((a, b) => a.guidanceDisplayOrder - b.guidanceDisplayOrder)
		.slice(0, 6);

	const {setShowModal, setModalProps} = useContext(HelixIntegrationContext);

	const onAnalyticsIconClick = (accountId) => {
		setShowModal(true);
		setModalProps({accountId, guidances, selectedStEntityId});
	};

	const trialBalance = params?.data?.trialBalanceData.find(
		(item) => item?.stEntityId === selectedStEntityId && item?.trialBalanceTypeId === trialBalanceType.Planning
	);

	if (
		(!engagement?.isMestEngagement || !isEqual(selectedStEntityId, 0)) &&
		isPrimary &&
		hasHelixComponentGuidance &&
		!isNullOrUndefined(trialBalance?.financialStatements?.[0]?.analysisPeriodBalance)
	) {
		return (
			<StyledAnalyticsIcons
				className={`analyticsIcons ${!hasHelixProjects ? 'disabled' : 'enabled'}`}
				title={hasHelixProjects ? labels.analytics : labels.analyticsIconDisabled}
				onClick={() => {
					hasHelixProjects && onAnalyticsIconClick(params.value);
				}}
			>
				<Icon src={IconoirStatsDownSquare} title={hasHelixProjects ? labels.analytics : labels.analyticsIconDisabled} />
			</StyledAnalyticsIcons>
		);
	} else {
		return <span className="noDataDash">{'-'}</span>;
	}
};

const StyledAnalyticsIcons = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	.motif-icon {
		display: flex;
	}
	svg {
		width: var(--px-24);
		height: var(--px-24);
		color: var(--neutrals-900);
		cursor: inherit;
	}
	&.disabled {
		cursor: default;
		svg {
			color: var(--neutrals-700);
		}
	}
`;